import type { ReactNode } from 'react';
import { cx } from '../cx';
import { NARROW_ONLY, WIDE_ONLY, type ChartLayout } from './layout';
import { ROUGH_DOT, ROUGH_FILL, SampleCount, sampleTier, type ThinLabels } from './thin';
import { LABEL_TEXT, TICK_TEXT, VALUE_TEXT, chartFill, fixedScale, niceScale, percentOf, thinTick } from './scale';

export type RangeRow = {
  id: string;
  label: ReactNode;
  /** The band's start and end: p25 and p75 for a middle half, or another pair the legend names (the median advertised minimum and maximum). */
  p25: number;
  /** The median tick. Leave out when the band is not a middle half and has no median. */
  median?: number;
  p75: number;
  /** One extra point on the row, drawn as a small ink diamond (a top-quartile reach); name it in the legend. */
  marker?: number;
  /** How many values the row is computed from: 20 or more draws the band, 10 to 19 a rough median, under 10 nothing. */
  sample: number;
  /** The figure written at the end of the band (the median, or what the row is about). */
  display: ReactNode;
};

export type RangeChartProps = {
  rows: RangeRow[];
  /** The row the headline is about: its band is sun, the rest context. */
  highlightId?: string;
  /** The scale; defaults to round numbers around every row. Several RangeCharts side by side share one. */
  scale?: { min: number; max: number };
  formatTick: (value: number) => string;
  /** The thin-data words ("rough", "Too few ads"), translated. */
  thinLabels: ThinLabels;
  /** A dashed reference line across every row (the official median, a Finland figure), labelled on the line. */
  reference?: { value: number; label: ReactNode };
  /** `auto` (default) switches to the narrow form in a narrow container: label and n on one line above each band. */
  layout?: ChartLayout;
  /** One sentence that states the finding and the key values, for screen readers. */
  description: string;
  className?: string;
};

/**
 * For each category, the middle half (p25 to p75) as a band and the median as an ink tick, on one
 * shared scale with round-number gridlines, and each row's n at its end. Thin rows use the shared
 * look: 10 to 19 values a hollow dashed median marked rough, under 10 no figure. MarketBar is its
 * one-row form, for one job.
 */
const ROW: Record<ChartLayout, string> = {
  auto: 'flex flex-col gap-0.5 @md:flex-row @md:items-center @md:gap-3',
  narrow: 'flex flex-col gap-0.5',
  wide: 'flex items-center gap-3',
};
const ROW_LABEL: Record<ChartLayout, string> = {
  auto: 'flex min-w-0 items-baseline gap-2 @md:w-1/4 @md:shrink-0',
  narrow: 'flex min-w-0 items-baseline gap-2',
  wide: 'flex w-1/4 min-w-0 shrink-0 items-baseline gap-2',
};
// In a column, flex-1 would collapse the plot to no height: the narrow form sizes it by width.
const PLOT: Record<ChartLayout, string> = { auto: '@md:flex-1', narrow: '', wide: 'flex-1' };

export function RangeChart({
  rows,
  highlightId,
  scale: fixed,
  formatTick,
  thinLabels,
  reference,
  layout = 'auto',
  description,
  className,
}: RangeChartProps) {
  const shown = rows.filter((r) => sampleTier(r.sample) !== 'none');
  // A rough row with a median shows only that median; one without shows its band, faint.
  const extent = (r: RangeRow): number[] => [
    ...(sampleTier(r.sample) === 'full' || r.median === undefined ? [r.p25, r.p75] : [r.median]),
    ...(r.marker !== undefined ? [r.marker] : []),
  ];
  const all = [...shown.flatMap(extent), ...(reference ? [reference.value] : [])];
  const scale = fixed ? fixedScale(fixed.min, fixed.max) : niceScale(Math.min(...all), Math.max(...all));
  const pos = (v: number) => percentOf(v, scale.min, scale.max);
  return (
    <div role="img" aria-label={description} className={cx('@container flex flex-col gap-2', className)}>
      <div className="flex gap-3" aria-hidden="true">
        <span className={cx('w-1/4 shrink-0', WIDE_ONLY[layout])} />
        <div className="relative mr-16 h-4 flex-1">
          {scale.ticks.map((t, i) => (
            <span
              key={t}
              className={cx('absolute -translate-x-1/2 whitespace-nowrap', TICK_TEXT, thinTick(i, scale.ticks.length))}
              style={{ left: `${pos(t)}%` }}
            >
              {formatTick(t)}
            </span>
          ))}
        </div>
        <span className={cx('w-12 shrink-0', WIDE_ONLY[layout])} />
      </div>
      <div className={cx('relative flex flex-col gap-2.5', reference && 'pt-5')}>
        {reference ? (
          <div
            className={cx('pointer-events-none absolute inset-y-0 right-31 left-1/4 z-10 ml-3', WIDE_ONLY[layout])}
            aria-hidden="true"
          >
            <span
              className="absolute inset-y-0 border-l-2 border-dashed border-jc-ink-secondary"
              style={{ left: `${pos(reference.value)}%` }}
            >
              <span
                className={cx(
                  'absolute top-0 text-xs font-semibold whitespace-nowrap text-jc-ink',
                  pos(reference.value) > 60 ? 'right-1.5' : 'left-1.5',
                )}
              >
                {reference.label}
              </span>
            </span>
          </div>
        ) : null}
        {/* One gridline set across all rows in the wide form; the narrow form draws its own inside each plot. */}
        <div
          className={cx('pointer-events-none absolute inset-y-0 right-31 left-1/4 ml-3', WIDE_ONLY[layout])}
          aria-hidden="true"
        >
          {scale.ticks.map((t) => (
            <span key={t} className="absolute inset-y-0 border-l border-jc-chart-grid" style={{ left: `${pos(t)}%` }} />
          ))}
        </div>
        {rows.map((r) => {
          const isHighlight = r.id === highlightId;
          const tier = sampleTier(r.sample);
          return (
            <div key={r.id} className={ROW[layout]}>
              <span className={ROW_LABEL[layout]}>
                <span className={cx('truncate', LABEL_TEXT, isHighlight && 'font-semibold text-jc-ink')}>
                  {r.label}
                </span>
                <SampleCount n={r.sample} className={cx('shrink-0', NARROW_ONLY[layout])} />
              </span>
              <div className={cx('relative mr-16 h-5', PLOT[layout])}>
                <div className={cx('pointer-events-none absolute inset-0', NARROW_ONLY[layout])} aria-hidden="true">
                  {scale.ticks.map((t) => (
                    <span
                      key={t}
                      className="absolute inset-y-0 border-l border-jc-chart-grid"
                      style={{ left: `${pos(t)}%` }}
                    />
                  ))}
                </div>
                {reference ? (
                  <span
                    className={cx(
                      'absolute -inset-y-1 z-10 border-l-2 border-dashed border-jc-ink-secondary',
                      NARROW_ONLY[layout],
                    )}
                    style={{ left: `${pos(reference.value)}%` }}
                    aria-hidden="true"
                  />
                ) : null}
                {r.marker !== undefined && tier !== 'none' ? (
                  <span
                    className="absolute top-1/2 z-10 size-2.5 -translate-1/2 rotate-45 rounded-xs bg-jc-ink ring-2 ring-jc-surface"
                    style={{ left: `${pos(r.marker)}%` }}
                    aria-hidden="true"
                  />
                ) : null}
                {tier === 'full' || (tier === 'rough' && r.median === undefined) ? (
                  <>
                    <span
                      className={cx(
                        'absolute top-1 h-3 origin-left rounded-jc-info motion-safe:animate-jc-grow-x',
                        chartFill(isHighlight ? 'highlight' : 'context'),
                        tier === 'rough' && ROUGH_FILL,
                      )}
                      style={{ left: `${pos(r.p25)}%`, width: `${pos(r.p75) - pos(r.p25)}%` }}
                    />
                    {r.median !== undefined ? (
                      <span
                        className="absolute inset-y-0 w-1 -translate-x-1/2 rounded-full bg-jc-ink"
                        style={{ left: `${pos(r.median)}%` }}
                      />
                    ) : null}
                    <span
                      className={cx('absolute top-1/2 -translate-y-1/2 pl-2', VALUE_TEXT)}
                      style={{ left: `${pos(Math.max(r.p75, r.marker ?? r.p75))}%` }}
                    >
                      {r.display}
                      {tier === 'rough' ? <>, {thinLabels.rough}</> : null}
                    </span>
                  </>
                ) : tier === 'rough' ? (
                  <>
                    <span
                      className={cx('absolute top-1/2 size-3 -translate-1/2', ROUGH_DOT)}
                      style={{ left: `${pos(r.median ?? r.p25)}%` }}
                    />
                    <span
                      className={cx('absolute top-1/2 -translate-y-1/2 pl-3', VALUE_TEXT, 'text-jc-ink-secondary')}
                      style={{ left: `${pos(r.median ?? r.p25)}%` }}
                    >
                      {r.display}, {thinLabels.rough}
                    </span>
                  </>
                ) : (
                  <span className="absolute top-1/2 left-0 -translate-y-1/2 text-xs text-jc-ink-secondary">
                    {thinLabels.tooFew}
                  </span>
                )}
              </div>
              <SampleCount n={r.sample} className={cx('w-12 shrink-0 text-right', WIDE_ONLY[layout])} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The legend for RangeChart and MarketBar, with translated labels: the band, the median tick, and
 * the row marker and reference line when the chart has them. Name the band for what it is ("Middle
 * half of ads", or "Median advertised minimum to maximum").
 */
export function RangeLegend({
  band,
  median,
  marker,
  reference,
}: {
  band: ReactNode;
  median?: ReactNode;
  marker?: ReactNode;
  reference?: ReactNode;
}) {
  return (
    <>
      <span className="inline-flex items-center gap-1.5">
        <span className="h-2 w-5 rounded-jc-info bg-jc-chart-context" />
        {band}
      </span>
      {median ? (
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-1 rounded-full bg-jc-ink" />
          {median}
        </span>
      ) : null}
      {marker ? (
        <span className="inline-flex items-center gap-1.5">
          <span className="size-2 rotate-45 rounded-xs bg-jc-ink" />
          {marker}
        </span>
      ) : null}
      {reference ? (
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 border-l-2 border-dashed border-jc-ink-secondary" />
          {reference}
        </span>
      ) : null}
    </>
  );
}
